import type { CSSProperties, ReactNode } from "react";

import type { PlayerRow } from "../game/types";
import { useT } from "../i18n";
import { AvatarDoJogador } from "../ui/avatar/Avatar";
import { strengthColor } from "../ui/atoms";

/**
 * A escalação desenhada no campo, como todo jogo de futebol faz.
 *
 * Antes eram quatro faixas de cartões retangulares, uma por setor. Elas diziam
 * a mesma coisa que isto diz, e diziam pior: quem joga escalação lê o time pela
 * forma dele, e "4-3-3" só vira quatro, três e três quando está desenhado.
 *
 * **A ficha carrega rosto, número e sobrenome**, nessa ordem de tamanho. É o
 * que identifica um jogador de longe, e é o que a ficha do elenco já mostra: o
 * rosto vem do mesmo banco de avatares, escolhido pelo id e pela nacionalidade,
 * então o zagueiro do campo é o mesmo sujeito da figurinha.
 *
 * **O campo é visto em perspectiva, e não de cima.** Desenhado reto, ele
 * esticava: a caixa preenche metade da tela e é quase quadrada, e um retângulo
 * de campo esticado nela vira uma piscina. Em perspectiva o desenho estreita
 * para o lado do ataque, com as linhas retas de um campo de verdade, que é como
 * qualquer jogo de futebol mostra escalação, e a mesma caixa passa a parecer um
 * campo.
 *
 * **O desenho e as fichas saem das mesmas duas funções.** `paraX` e `paraY`
 * levam a posição do campo para a posição na tela, e tanto a linha branca
 * quanto o jogador em cima dela passam por elas. Com duas contas separadas, o
 * atacante ficaria fora da linha do ataque assim que alguém mexesse numa
 * delas.
 */

/**
 * Quanto o campo estreita no fundo, em fração da largura da frente.
 *
 * É a perspectiva inteira: 0,72 quer dizer que a linha de fundo do ataque tem
 * setenta e dois por cento da largura da linha de fundo do nosso gol. Menos que
 * isso vira um funil e o ataque fica sem espaço para quatro jogadores; mais,
 * e o campo volta a parecer reto.
 */
const ESTREITAMENTO = 0.72;

/** Onde o gramado começa e acaba dentro da caixa, em porcentagem. */
const MARGEM = { topo: 3, fundo: 97 };

/**
 * Onde param a linha mais recuada e a mais adiantada, e o goleiro.
 *
 * A profundidade vai de 0 na linha de fundo do ataque a 1 na do nosso gol: o
 * gol fica embaixo e o ataque em cima, que é como o campo do seu time é
 * mostrado em todo lugar. As linhas do meio se espalham entre `defesa` e
 * `ataque`, quantas a formação tiver, e por isso não há número fixo para elas
 * aqui: um 4-2-3-1 tem quatro linhas de linha onde um 4-4-2 tem três.
 *
 * O goleiro para em 0,90 e não em 0,96: a ficha é centrada na profundidade
 * dela, e mais fundo que isso a tarja do nome passava da linha e saía cortada
 * pela borda da caixa.
 */
const FAIXA = { defesa: 0.7, ataque: 0.17, goleiro: 0.9 };

/**
 * De que lado do campo cada posição fica.
 *
 * As vagas chegam na ordem em que a formação as escreveu, que não é a ordem em
 * que elas aparecem no campo: um 4-3-3 lista zagueiro, zagueiro, lateral
 * direito, lateral esquerdo, e desenhado assim daria os dois laterais juntos
 * no canto. Aqui a lateral direita vai para a direita e a esquerda para a
 * esquerda, e o resto fica no meio, na ordem em que veio.
 */
const LADO: Record<string, number> = { LE: -1, PE: -1, LD: 1, PD: 1 };

/**
 * O setor de cada posição, usado só quando o nome da formação não diz nada.
 *
 * Esta tabela já foi a regra, e era errada: ela diz que `PD` é ataque, o que
 * vale no 4-3-3 e não vale no 4-4-2, onde o `PD` é o meia pela direita. Ficou
 * como rede de segurança para formação de nome ilegível, que hoje não existe.
 */
const SETOR_DA_POSICAO: Record<string, string> = {
  ZAG: "DEF",
  LD: "DEF",
  LE: "DEF",
  VOL: "MEI",
  MC: "MEI",
  MEI: "MEI",
  PD: "ATA",
  PE: "ATA",
  ATA: "ATA",
};

/** Uma vaga da formação: que posição é, e em que índice da escalação. */
export interface VagaNoCampo {
  pos: string;
  indice: number;
}

/**
 * Quantos jogadores em cada linha, lido do nome da formação.
 *
 * Devolve `null` quando o nome não fecha com as vagas, e aí quem chama cai no
 * setor da posição.
 */
function contagemDoNome(nome: string, vagasDeLinha: number): number[] | null {
  const numeros = nome.split("-").map((parte) => Number(parte));
  if (numeros.length < 2) return null;
  if (numeros.some((n) => !Number.isInteger(n) || n < 1)) return null;
  if (numeros.reduce((soma, n) => soma + n, 0) !== vagasDeLinha) return null;
  return numeros;
}

/** A profundidade da enésima linha, espalhando da defesa até o ataque. */
function profundidadeDa(faixa: number, total: number): number {
  if (total < 2) return (FAIXA.defesa + FAIXA.ataque) / 2;
  return FAIXA.defesa + (FAIXA.ataque - FAIXA.defesa) * (faixa / (total - 1));
}

/**
 * As vagas agrupadas em linhas, do nosso gol para o ataque.
 *
 * **Quem diz de que linha é cada vaga é o nome da formação, e não a sigla da
 * posição.** A mesma sigla muda de linha conforme a formação: num 4-4-2 o `PD`
 * é o meia pela direita e num 4-3-3 é o ponta. Classificando por sigla, os dois
 * iam para o ataque, e o 4-4-2 saía desenhado com quatro na frente e dois no
 * meio, que é um 4-2-4.
 *
 * O nome serve porque as vagas vêm na ordem do gol para o ataque em toda
 * formação do motor, então "4-4-2" recorta a lista direto. A soma conferir com
 * o número de vagas é o que garante que o nome e as vagas ainda falam do mesmo
 * time; quando não fecha, o setor da posição assume.
 *
 * A ordenação dentro da linha é estável no índice para as posições do meio não
 * trocarem de lugar entre uma renderização e outra: dois zagueiros têm o mesmo
 * lado, e sem o desempate eles poderiam piscar de um lado para o outro.
 */
export function faixasDaEscalacao(
  formacao: string,
  slots: string[],
): { v: number; vagas: VagaNoCampo[] }[] {
  const todas: VagaNoCampo[] = slots.map((pos, indice) => ({ pos, indice }));
  const goleiros = todas.filter((vaga) => vaga.pos === "GOL");
  const deLinha = todas.filter((vaga) => vaga.pos !== "GOL");

  const contagem = contagemDoNome(formacao, deLinha.length);
  const faixas: VagaNoCampo[][] = [];
  if (contagem) {
    let de = 0;
    for (const quantos of contagem) {
      faixas.push(deLinha.slice(de, de + quantos));
      de += quantos;
    }
  } else {
    for (const setor of ["DEF", "MEI", "ATA"]) {
      faixas.push(deLinha.filter((vaga) => SETOR_DA_POSICAO[vaga.pos] === setor));
    }
  }

  const emOrdem = (vagas: VagaNoCampo[]): VagaNoCampo[] =>
    [...vagas].sort((a, b) => (LADO[a.pos] ?? 0) - (LADO[b.pos] ?? 0) || a.indice - b.indice);

  return [
    { v: FAIXA.goleiro, vagas: goleiros },
    ...faixas.map((vagas, faixa) => ({
      v: profundidadeDa(faixa, faixas.length),
      vagas: emOrdem(vagas),
    })),
  ].filter((linha) => linha.vagas.length > 0);
}

/**
 * Da posição no campo para a posição na caixa.
 *
 * `u` atravessa o campo, de 0 na lateral esquerda a 1 na direita. `v` é a
 * profundidade, de 0 na linha de fundo do ataque a 1 na do nosso gol. As duas
 * devolvem porcentagem da caixa, que é o que o CSS e o SVG entendem.
 */
function larguraEm(v: number): number {
  return ESTREITAMENTO + (1 - ESTREITAMENTO) * v;
}

function paraX(u: number, v: number): number {
  return 50 + (u - 0.5) * larguraEm(v) * 100;
}

/*
 * As linhas são retas, como num campo de verdade.
 *
 * Elas já arquearam no meio para tirar o aspecto de diagrama, e o resultado
 * parecia campo visto por lente olho de peixe: a linha de fundo e a do meio
 * curvavam e os jogadores de uma mesma linha saíam em arco. A perspectiva do
 * estreitamento basta para o desenho parecer campo.
 */
function paraY(_u: number, v: number): number {
  return MARGEM.topo + v * (MARGEM.fundo - MARGEM.topo);
}

/**
 * Uma linha de lateral a lateral.
 *
 * Continua escrita como curva quadrática porque `caixaNoCampo` emenda os
 * pedaços pelo `Q`; com as linhas retas a flecha é zero e a curva sai reta.
 */
function linhaEm(v: number, de = 0, ate = 1): string {
  const meio = (de + ate) / 2;
  const flecha = paraY(meio, v) - (paraY(de, v) + paraY(ate, v)) / 2;
  return (
    `M ${String(paraX(de, v))} ${String(paraY(de, v))} ` +
    `Q ${String(paraX(meio, v))} ${String(paraY(meio, v) + flecha)} ` +
    `${String(paraX(ate, v))} ${String(paraY(ate, v))}`
  );
}

/** Um retângulo do campo, em perspectiva. */
function caixaNoCampo(de: number, ate: number, v0: number, v1: number): string {
  return (
    `${linhaEm(v0, de, ate)} ` +
    `L ${String(paraX(ate, v1))} ${String(paraY(ate, v1))} ` +
    `${linhaEm(v1, ate, de).replace(/^M [^Q]+Q/, "Q")} ` +
    `L ${String(paraX(de, v0))} ${String(paraY(de, v0))} Z`
  );
}

/**
 * O sobrenome, que é como jogador é chamado nas costas da camisa.
 *
 * Nome inteiro não cabe numa ficha de setenta pixels, e cortar no meio dá
 * "Anderson Gue...". Apelido de uma palavra só fica como está, que é o caso da
 * maioria dos brasileiros gerados.
 */
export function nomeCurto(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return partes.length === 1 ? nome : (partes[partes.length - 1] as string);
}

/**
 * O quanto o bloco do time anda com a postura.
 *
 * Estilo empurra as linhas de linha para frente ou para trás, e marcação
 * aproxima ou afasta uma da outra. O goleiro fica onde está. É o desenho dizer
 * o que o botão mudou, sem frase explicando: ofensivo sobe o time, marcação
 * forte deixa o bloco compacto.
 */
const DESLOCAMENTO = { ofensivo: -0.06, equilibrado: 0, defensivo: 0.06 } as const;
const APERTO = { leve: 1.08, normal: 1, forte: 0.84 } as const;

function comPostura(v: number, estilo?: keyof typeof DESLOCAMENTO, marcacao?: keyof typeof APERTO): number {
  if (v === FAIXA.goleiro) return v;
  const centro = (FAIXA.defesa + FAIXA.ataque) / 2;
  const novo = centro + (v - centro) * APERTO[marcacao ?? "normal"] + DESLOCAMENTO[estilo ?? "equilibrado"];
  return Math.min(0.8, Math.max(0.12, novo));
}

export function EscalacaoNoCampo({
  formacao,
  slots,
  starters,
  porId,
  rendimentoDe,
  aoEscolher,
  estilo,
  marcacao,
  classeDa,
  marcasDa,
  rotuloDa,
  children,
}: {
  /** O nome da formação, que é quem diz quantos jogadores tem cada linha. */
  formacao: string;
  /** As posições da formação, na ordem em que ela as declara. */
  slots: string[];
  /** Quem está em cada vaga, no mesmo índice de `slots`. */
  starters: string[];
  porId: Map<string, PlayerRow>;
  rendimentoDe: (playerId: string, pos: string) => number;
  aoEscolher: (indiceDaVaga: number) => void;
  estilo?: keyof typeof DESLOCAMENTO;
  marcacao?: keyof typeof APERTO;
  /** Estado de cada vaga no pegar e soltar: pega, destino, apagada. */
  classeDa?: (indiceDaVaga: number) => string;
  /** Selos e alertas em cima do retrato. */
  marcasDa?: (indiceDaVaga: number) => ReactNode;
  rotuloDa?: (indiceDaVaga: number) => string;
  /** O que mora em cima do gramado: formação, postura, força dos setores. */
  children?: ReactNode;
}): ReactNode {
  const t = useT();

  const linhas = faixasDaEscalacao(formacao, slots).map((linha) => ({
    ...linha,
    v: comPostura(linha.v, estilo, marcacao),
  }));

  return (
    <div className="campo">
      <Gramado />
      {children}

      {linhas.map(({ v, vagas }) =>
        vagas.map((vaga, ordem) => {
          const id = starters[vaga.indice];
          const jogador = id ? porId.get(id) : undefined;
          const rendimento = id ? rendimentoDe(id, vaga.pos) : 0;
          const improvisado = jogador && jogador.pos !== vaga.pos;

          /*
           * A faixa útil não é o campo inteiro: as fichas param antes da linha
           * lateral, senão a do ponta sai metade para fora do gramado.
           */
          const u = 0.08 + ((ordem + 0.5) / vagas.length) * 0.84;

          return (
            <button
              key={vaga.indice}
              type="button"
              className={`campo-vaga${improvisado ? " improvisado" : ""} ${classeDa?.(vaga.indice) ?? ""}`}
              data-lista="t"
              data-i={vaga.indice}
              onClick={() => aoEscolher(vaga.indice)}
              title={jogador ? jogador.name : t("tatica.vagaAberta")}
              aria-label={rotuloDa?.(vaga.indice)}
              style={{ left: `${String(paraX(u, v))}%`, top: `${String(paraY(u, v))}%` }}
            >
              {/*
                A foto e a força são irmãs, e a foto é que é redonda.

                Com a força dentro do círculo, o `overflow` do círculo comia
                metade dela: lia-se "4" onde estava escrito "43". Aqui o
                círculo recorta só o retrato, e a medalha fica por fora dele.
              */}
              <span
                className="campo-foto"
                style={jogador ? ({ "--cond": jogador.condition } as CSSProperties) : undefined}
                data-cond={jogador ? faixaDeCondicao(jogador.condition) : undefined}
              >
                <span
                  className="campo-retrato"
                  data-vazia={jogador ? undefined : ""}
                >
                  {jogador ? (
                    <AvatarDoJogador
                      playerId={jogador.id}
                      nacionalidade={jogador.nationality}
                      rosto
                    />
                  ) : (
                    <span className="campo-posicao">{vaga.pos}</span>
                  )}
                </span>
                {jogador && (
                  <span className="campo-forca" style={{ background: strengthColor(rendimento) }}>
                    {rendimento}
                  </span>
                )}
                {marcasDa?.(vaga.indice)}
              </span>

              <span className="campo-nome">
                {jogador?.shirt !== null && jogador?.shirt !== undefined && (
                  <span className="campo-numero">{jogador.shirt}</span>
                )}
                {jogador ? nomeCurto(jogador.name) : t("tatica.vagaAberta")}
              </span>
            </button>
          );
        }),
      )}
    </div>
  );
}

/**
 * A cor do anel de condição: verde inteiro, amarelo gasto, vermelho no limite.
 *
 * Os cortes são os mesmos do alerta de cansaço, para o anel e o raio nunca
 * discordarem sobre o mesmo jogador.
 */
export function faixaDeCondicao(condicao: number): "boa" | "gasta" | "baixa" {
  return condicao >= 85 ? "boa" : condicao >= 70 ? "gasta" : "baixa";
}

/**
 * O gramado e as linhas, em perspectiva.
 *
 * A caixa do SVG é de 100 por 100 com `preserveAspectRatio="none"`, e isso é
 * de propósito: aqui não se desenha um campo em metros, se desenha um campo na
 * caixa que a tela deu. Toda medida é porcentagem dela, então o desenho encaixa
 * em qualquer formato de coluna sem esticar, porque não há proporção real a
 * respeitar.
 *
 * As faixas de corte de grama seguem a mesma perspectiva das linhas. Elas são o
 * que faz o desenho parecer campo e não diagrama.
 */
function Gramado(): ReactNode {
  const faixas = [0, 1, 2, 3, 4, 5];

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="campo-grama" aria-hidden="true">
      <rect width="100" height="100" fill="#14532a" />

      {faixas.map((faixa) => {
        const v0 = faixa / faixas.length;
        const v1 = (faixa + 1) / faixas.length;
        return (
          <path
            key={faixa}
            d={caixaNoCampo(0, 1, v0, v1)}
            fill={faixa % 2 === 0 ? "#1d6b33" : "#1a6130"}
          />
        );
      })}

      <g fill="none" stroke="#ffffff" strokeOpacity="0.5" strokeWidth="0.35" strokeLinejoin="round">
        <path d={caixaNoCampo(0, 1, 0, 1)} />
        <path d={linhaEm(0.5)} />
        {/* Área grande e pequena, nas duas pontas. */}
        <path d={caixaNoCampo(0.22, 0.78, 0, 0.15)} />
        <path d={caixaNoCampo(0.37, 0.63, 0, 0.05)} />
        <path d={caixaNoCampo(0.22, 0.78, 0.85, 1)} />
        <path d={caixaNoCampo(0.37, 0.63, 0.95, 1)} />
        {/*
          O círculo central é uma elipse achatada, e não um círculo.

          Visto de cima ele é redondo; visto de onde esta tela olha, ele deita.
          Um círculo de verdade aqui denunciaria que o resto é perspectiva e ele
          não.
        */}
        <ellipse cx={paraX(0.5, 0.5)} cy={paraY(0.5, 0.5)} rx="11" ry="6.5" />
      </g>

      <g fill="#ffffff" fillOpacity="0.5">
        <circle cx={paraX(0.5, 0.5)} cy={paraY(0.5, 0.5)} r="0.6" />
        <circle cx={paraX(0.5, 0.1)} cy={paraY(0.5, 0.1)} r="0.6" />
        <circle cx={paraX(0.5, 0.9)} cy={paraY(0.5, 0.9)} r="0.6" />
      </g>
    </svg>
  );
}
